import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useParams } from "react-router";

import api from "@app/analytics/MeetingReport/api";
import { DEFAULT_COACHING_DATA } from "@app/analytics/MeetingReport/api/parsingUtils";
import { Moment } from "@app/analytics/MeetingReport/components/Moments/types";
import useMeetingReport from "@app/analytics/MeetingReport/hooks/useMeetingReport";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import useUserAttendance from "@app/analytics/MeetingReport/hooks/useUserAttendance";
import useVideoStore from "@app/analytics/MeetingReport/stores/video";
import {
  CoachingData,
  CoachingDataWithoutTimestamps,
  MomentWithoutTimestamps,
} from "@app/analytics/MeetingReport/types";
import { PROD_SAMPLE_MEETING } from "@app/analytics/sampleData/sampleMeetings";
import useAuth from "@app/common/auth/useAuth";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import { getRelativeTimestamp } from "@app/common/utils";

import { SAMPLE_COACHING_DATA } from "./sampleData";

const makePopulateMoment =
  (meetingStartTime: DateTime, videoStartTimeOffset: number) =>
  (moment: MomentWithoutTimestamps, offset = 0): Moment => {
    const timestamp = getRelativeTimestamp(meetingStartTime, moment.startTime);
    const relativeStartTime = moment.startTime
      .diff(meetingStartTime)
      .plus({ seconds: videoStartTimeOffset + offset })
      .as("seconds");

    return {
      ...moment,
      timestamp,
      relativeStartTime,
    };
  };

/** Populates relative values on coaching data (e.g. relative timestamps, relative start times) */
const populateCoachingData = (
  data: CoachingDataWithoutTimestamps,
  meetingStartTime: DateTime,
  videoStartTimeOffset: number
): CoachingData => {
  const populateMoment = makePopulateMoment(
    meetingStartTime,
    videoStartTimeOffset
  );

  const populatedFillerWords = {
    ...data.fillerWords,
    moments: data.fillerWords.moments.map((m) => populateMoment(m, -1)),
  };

  const populatedInterruptions = {
    ...data.interruptions,
    // offset the relative start time by a couple seconds to ensure we capture the start of the interruption
    moments: data.interruptions.moments.map((m) => populateMoment(m, -2)),
  };

  const populatedNonInclusiveTerms = {
    ...data.nonInclusiveTerms,
    moments: data.nonInclusiveTerms.moments.map((m) => populateMoment(m, -1)),
  };

  const populatedQuestions = {
    ...data.questions,
    moments: data.questions.moments.map((m) => populateMoment(m, -1)),
  };

  const populatedTalkingPace = {
    ...data.talkingPace,
    moments: data.talkingPace.moments.map(populateMoment),
  };

  return {
    fillerWords: populatedFillerWords,
    interruptions: populatedInterruptions,
    nonInclusiveTerms: populatedNonInclusiveTerms,
    questions: populatedQuestions,
    talkingPace: populatedTalkingPace,
  };
};

const useCoachingData = () => {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const { canAccessCoaching } = useFeatureAccess();

  const isSampleReport = sessionId === PROD_SAMPLE_MEETING.id;

  const { meetingStartTime, isLoading: isMeetingReportLoading } =
    useMeetingReport();
  const {
    data: { isRestrictedReport },
  } = useSessionMetadata({ enabled: canAccessCoaching });

  const { wasInMeeting } = useUserAttendance({
    enabled: canAccessCoaching && !isRestrictedReport,
  });

  const videoStartTimeOffset = useVideoStore(
    (state) => state.videoStartTimeOffset
  );

  const {
    data,
    isLoading: isCoachingLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["coaching", sessionId, user?.id],
    queryFn: () => api.data.getCoachingData(sessionId!, user!.id),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled:
      !isSampleReport &&
      canAccessCoaching &&
      Boolean(user?.id) &&
      wasInMeeting &&
      Boolean(sessionId) &&
      !isRestrictedReport,
  });

  const populatedCoachingData = useMemo(() => {
    if (isSampleReport) return SAMPLE_COACHING_DATA;

    if (!meetingStartTime || !data) {
      return DEFAULT_COACHING_DATA as CoachingData;
    }

    return populateCoachingData(data, meetingStartTime, videoStartTimeOffset);
  }, [
    isMeetingReportLoading,
    isCoachingLoading,
    videoStartTimeOffset,
    isSampleReport,
  ]);

  const isInitialLoading = isMeetingReportLoading || isCoachingLoading;

  // if there wasn't enough talk time to have been able to determine a pace,
  // all the other metrics are probably going to be empty as well
  const insufficientTalkTimeData =
    !isInitialLoading && !populatedCoachingData.talkingPace.rate;

  return {
    ...populatedCoachingData,
    isInitialLoading,
    isError,
    insufficientTalkTimeData,
    refetch,
  };
};

export default useCoachingData;
